import React, { useEffect, useState } from 'react'

import { PluckingRooster as PluckingRoosterGame, ASSETS as ROOSTER_ASSETS } from './PluckingRooster/PluckingRooster'
import { SubmitGame as BLSubmitGame, ValidatePlayer as BLValidatePlayer, TrackMetric as BLTrackMetric, GetSettings as BLGetSettings } from './../../../utilities/BL/Admin/Games/PluckingRooster'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { ValidateSqlInjection as BLValidateSqlInjection } from './../../../utilities/BL/Security'

const SOURCE_MAPPING = {
    'l': 'En vivo',
    'g': 'Partido',
    'obs': 'OBS Streaming',
    'screen': 'Pantalla Estadio',
    'qr': 'Código QR'
};

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA DINÁMICA
1. Organizador de la dinámica: El Club Atlético de San Luis es el único responsable organizador de esta dinámica y de la entrega física del premio.
2. Deslinde de responsabilidad técnica: Five Digital Minds e Interaktia actúan únicamente como los proveedores tecnológicos de la plataforma del juego y queda eximido de cualquier responsabilidad derivada de la entrega de premios o controversias de la dinámica.
3. Participantes: Dinámica válida únicamente para aficionados presentes en el estadio que sean mayores de edad.
4. Registro: Cada participante puede registrarse una sola vez. Intentos múltiples serán anulados.
5. Elección del ganador: Los ganadores se elegirán con base en la puntuación más alta de aciertos y, en caso de empate, por el menor tiempo de registro o participación.
6. Decisiones: Las decisiones del comité organizador son inapelables y definitivas.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono) serán recabados con la única finalidad de registrar su participación en la dinámica, poder contactarle y validar su identidad en caso de resultar ganador, así como para estadísticas internas de participación de los organizadores.

Al aceptar este aviso, autoriza expresamente que sus datos de contacto sean compartidos y transferidos entre los patrocinadores, el Atlético de San Luis y la empresa operadora Five Digital Minds, así como sus asociados comerciales y aliados estratégicos con fines de marketing, estadísticas y entrega de premios. No se realizarán transferencias a otros terceros sin su consentimiento. Para dudas o aclaraciones contacte a soporte.`;

const PluckingRooster = () => {
    const { showMessage } = useFloatMessage();

    // Estado del flujo
    const [step, setStep] = useState('register'); // register, game, sending, done, played, resendPoints, inactive
    const [userData, setUserData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        companionType: '', // Valor por defecto vacío para obligar a seleccionar
        abonado: '' // Opciones: 'si', 'no'
    });
    const [finalScore, setFinalScore] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [formStartTracked, setFormStartTracked] = useState(false);

    // Estados para Términos, Aviso de Privacidad y Modales legales
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // 1. Manejo de Sesión y Métricas
    const getSessionId = () => {
        let sessId = sessionStorage.getItem('rooster_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('rooster_session_id', sessId);
        }
        return sessId;
    };

    const getSource = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const paramSrc = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src');
        if (paramSrc) {
            sessionStorage.setItem('rooster_source', paramSrc);
            return SOURCE_MAPPING[paramSrc] || paramSrc;
        }
        const savedSrc = sessionStorage.getItem('rooster_source');
        if (savedSrc) {
            return SOURCE_MAPPING[savedSrc] || savedSrc;
        }
        return 'Directo';
    };

    const trackEvent = async (eventName) => {
        const sessId = getSessionId();
        const srcVal = getSource();

        try {
            await BLTrackMetric(eventName, sessId, srcVal);
        } catch (e) {
            console.error('Local tracking error:', e);
        }

        try {
            if (window.gtag) {
                window.gtag('event', `rooster_${eventName}`, {
                    session_id: sessId,
                    event_category: 'rooster_game',
                    event_label: eventName,
                    traffic_source: srcVal
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    };

    const handleInputChange = (e) => {
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    // VALIDACIÓN AL CARGAR (LocalStorage y métricas iniciales) y PRECARGA DE RECURSOS
    useEffect(() => {
        // Precarga de imágenes y recursos del juego en segundo plano mientras el usuario se registra
        if (ROOSTER_ASSETS) {
            Object.values(ROOSTER_ASSETS).forEach((src) => {
                if (typeof src === 'string') {
                    if (src.endsWith('.png') || src.endsWith('.jpg') || src.endsWith('.jpeg') || src.endsWith('.webp') || src.endsWith('.gif')) {
                        const img = new Image();
                        img.src = src;
                    } else if (src.endsWith('.mp3') || src.endsWith('.wav') || src.endsWith('.ogg')) {
                        const audio = new Audio();
                        audio.src = src;
                        audio.preload = 'auto';
                    }
                }
            });
        }
        const bannerImg = new Image();
        bannerImg.src = '/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif';

        // const played = localStorage.getItem('rooster_played');

        // if (played === 'true') {
        //     setStep('played');
        // }

        const trackInitialEventsAndCheckActive = async () => {
            const settingsRes = await BLGetSettings();
            if (settingsRes.success && settingsRes.data) {
                const isGameActive = settingsRes.data.isGameActive ?? true;
                if (!isGameActive) {
                    setStep('inactive');
                    return;
                }
            }

            const hasPageView = sessionStorage.getItem('rooster_page_view_tracked');
            if (!hasPageView) {
                sessionStorage.setItem('rooster_page_view_tracked', 'true');
                await trackEvent('page_view');
            }

            const urlParams = new URLSearchParams(window.location.search);
            const sourceParam = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src');
            if (sourceParam) {
                sessionStorage.setItem('rooster_source', sourceParam);
                const hasQrScan = sessionStorage.getItem('rooster_qr_scan_tracked');
                if (!hasQrScan) {
                    sessionStorage.setItem('rooster_qr_scan_tracked', 'true');
                    await trackEvent('qr_scan');
                }
            }
        };
        trackInitialEventsAndCheckActive();

        return () => {
            const gameContainer = document.getElementById('PluckingRooster__Game');
            if (gameContainer) gameContainer.style.overflow = 'visible';
        }
    }, []);

    const handleRegister = async (e) => {
        e.preventDefault();
        
        // 1. Validar campos vacíos y formatos (Regex)
        if (!userData.fullName.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El nombre completo es requerido.` });
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!userData.email.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El correo electrónico es requerido.` });
            return;
        } else if (!emailRegex.test(userData.email.trim())) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El correo electrónico no tiene un formato válido.` });
            return;
        }

        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!userData.phoneNumber.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El teléfono / WhatsApp es requerido.` });
            return;
        } else if (!phoneRegex.test(userData.phoneNumber.trim())) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El teléfono debe ser numérico y contener entre 10 y 15 dígitos.` });
            return;
        }

        // if (!userData.companionType) {
        //     showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor, selecciona tu zona para jugar.` });
        //     return;
        // }

        if (!userData.abonado || (userData.abonado !== 'si' && userData.abonado !== 'no')) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Por favor, selecciona si eres abonado o no.' });
            return;
        }

        if (!acceptedTerms) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Debes aceptar los Términos y Condiciones y el Aviso de Privacidad para jugar.` });
            return;
        }

        setIsLoading(true);

        // 2. Validación de Inyección SQL
        const isNameThreat = await BLValidateSqlInjection(userData.fullName);
        const isEmailThreat = await BLValidateSqlInjection(userData.email);
        const isPhoneThreat = await BLValidateSqlInjection(userData.phoneNumber);
        const isAbonadoThreat = await BLValidateSqlInjection(userData.abonado);

        if (isNameThreat || isEmailThreat || isPhoneThreat || isAbonadoThreat) {
            setIsLoading(false);
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Caracteres no permitidos en el formulario.` });
            return;
        }

        // 3. VALIDAMOS CONTRA EL BACKEND
        const validation = await BLValidatePlayer(userData.email.trim(), userData.phoneNumber.trim());

        setIsLoading(false);

        if (!validation.success) {
            setStep('played');
            return;
        }

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'hidden';
        
        // Registrar inicio de dinámica exitoso
        await trackEvent('form_complete');
        await trackEvent('game_start');
        setStep('game');
    };
    
    // 2. Callback cuando el juego termina
    const handleGameFinish = async (score) => {
        await new Promise(resolve => setTimeout(resolve, 3000));

        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setFinalScore(score);
        setStep('sending');

        const result = await BLSubmitGame(userData, score, acceptedTerms, getSource());

        if (result.success) {
            await trackEvent('game_complete');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntaje enviado con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar enviar tu puntaje. Inténtalo nuevamente.' });
            setStep('resendPoints'); 
        }
    };

    const ResendPoints = async () => {
        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setStep('sending');

        const result = await BLSubmitGame(userData, finalScore, acceptedTerms, getSource());

        if (result.success) {
            await trackEvent('game_complete');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntuación enviada con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: '¡Ups! Ha habido un error al intentar enviar tu puntaje' });
            setStep('resendPoints'); 
        }
    };

    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div id='PluckingRooster__Game' style={{ width: '100vw', height: '100vh' }}>
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            {step === 'register' && (
                <div 
                    className="w-100 min-vh-100 d-flex flex-column justify-content-between py-4 px-3"
                    style={{
                        backgroundImage: 'url(/img/page/backgrounds/barrio9/background-1.png)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: '#0f172a'
                    }}
                >
                    <div className="container d-flex flex-column align-items-center justify-content-center py-2" style={{ maxWidth: '450px' }}> 
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mb-3" alt="Logo" style={{ maxHeight: '70px', objectFit: 'contain' }} />
                        
                        <div className="text-center text-white mb-3">
                            <p className="SelfieFan__Fan__Title text-white fw-bold mb-1">¡Bienvenid@ Aficionad@!</p>
                            <p className="SelfieFan__Fan__Text--Center text-white mb-1">Participa por un regalo especial.</p>
                            <p className='Messages__Fan__Text--Center text-white mb-1'>Obtén puntos cada vez que le pegues al gallo</p>
                            <p className='Messages__Fan__Text--Center text-white mb-0' style={{ fontSize: '1em' }}>La persona que obtenga más puntos y más rápido será ganador(a)</p>
                        </div>

                        {/* CONTENEDOR CON FONDO BLANCO */}
                        <div 
                            className="w-100 bg-white rounded-4 shadow-lg p-4 text-dark"
                            style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '1.25rem',
                                color: '#000000',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)'
                            }}
                        >
                            <h2 className='text-center fw-bold text-dark fs-5 mb-3' style={{ color: '#000000' }}>¡Únete al juego!</h2>

                            <div className="mb-3 text-start">
                                <label className='form-label small fw-bold text-dark mb-1' htmlFor='fullName' style={{ color: '#000000' }}>
                                    Nombre completo
                                </label>
                                <input 
                                    id="fullName" 
                                    name="fullName" 
                                    type="text"
                                    //placeholder="Escribe tu nombre y apellido" 
                                    onChange={handleInputChange} 
                                    onFocus={handleInputFocus} 
                                    className="form-control"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#000000',
                                        borderColor: '#cbd5e1',
                                        borderRadius: '8px',
                                        padding: '0.65rem 0.85rem'
                                    }}
                                />
                            </div>

                            <div className="mb-3 text-start">
                                <label className='form-label small fw-bold text-dark mb-1' htmlFor='email' style={{ color: '#000000' }}>
                                    Correo electrónico
                                </label>
                                <input 
                                    id="email" 
                                    name="email" 
                                    type="email"
                                    //placeholder="ejemplo@correo.com" 
                                    onChange={handleInputChange} 
                                    onFocus={handleInputFocus} 
                                    className="form-control"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#000000',
                                        borderColor: '#cbd5e1',
                                        borderRadius: '8px',
                                        padding: '0.65rem 0.85rem'
                                    }}
                                />
                            </div>

                            <div className="mb-3 text-start">
                                <label className='form-label small fw-bold text-dark mb-1' htmlFor='phoneNumber' style={{ color: '#000000' }}>
                                    WhatsApp o Teléfono (10 dígitos)
                                </label>
                                <input 
                                    id="phoneNumber" 
                                    name="phoneNumber" 
                                    type="tel"
                                    maxLength={15}
                                    //placeholder="Ej. 4441234567" 
                                    onChange={handleInputChange} 
                                    onFocus={handleInputFocus} 
                                    className="form-control"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        color: '#000000',
                                        borderColor: '#cbd5e1',
                                        borderRadius: '8px',
                                        padding: '0.65rem 0.85rem'
                                    }}
                                />
                            </div>

                            <div className="mb-3 text-start">
                                <label className='form-label small fw-bold text-dark mb-1' htmlFor='abonado' style={{ color: '#000000' }}>
                                    ¿Eres abonado?
                                </label>
                                <select 
                                    id="abonado" 
                                    name="abonado" 
                                    value={userData.abonado} 
                                    onChange={handleInputChange} 
                                    onFocus={handleInputFocus} 
                                    className='form-select'
                                    style={{ 
                                        cursor: 'pointer', 
                                        backgroundColor: '#ffffff', 
                                        color: '#000000',
                                        borderColor: '#cbd5e1',
                                        borderRadius: '8px',
                                        padding: '0.65rem 0.85rem'
                                    }}
                                    required
                                >
                                    <option value="" disabled style={{ color: '#9ca3af' }}>Selecciona una opción</option>
                                    <option value="si" style={{ color: '#000000' }}>Sí</option>
                                    <option value="no" style={{ color: '#000000' }}>No</option>
                                </select>
                            </div>

                            <div className="form-check my-3 text-start d-flex align-items-start gap-2">
                                <input 
                                    className="form-check-input mt-1" 
                                    type="checkbox" 
                                    id="acceptTermsCheck" 
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                    style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer', flexShrink: 0 }}
                                />
                                <label className="form-check-label" htmlFor="acceptTermsCheck" style={{ fontSize: '0.78rem', lineHeight: '1.4', cursor: 'pointer', color: '#000000' }}>
                                    He leído y acepto los <button type="button" className="btn btn-link p-0 text-danger text-decoration-underline small border-0 align-baseline fw-bold" style={{ fontSize: '0.78rem', color: '#cc3723' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-danger text-decoration-underline small border-0 align-baseline fw-bold" style={{ fontSize: '0.78rem', color: '#cc3723' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la dinámica.
                                </label>
                            </div>

                            {isLoading ? (
                                <button className="Btn__Adsl--RedOutline w-100 py-2 mt-3" disabled={true}>Iniciando juego...</button>
                            ) : (
                                <button onClick={handleRegister} className="Btn__Adsl--Red w-100 py-2 mt-3 fw-bold">Jugar ahora</button>
                            )}
                        </div>
                    </div>

                    <footer className="text-center mt-3 FooterCredits text-white" style={{ fontSize: '1em' }}>
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-white text-decoration-underline'>Interaktia</a>
                    </footer>
                </div>
            )}

            {step === 'game' && (
                <PluckingRoosterGame onGameFinish={handleGameFinish} initialDuration={15} />
            )}

            {step === 'sending' && (
                <>
                    <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="SelfieFan__Fan__Title mt-3">¡Muy bien{(userData && userData?.fullName && userData.fullName !== '') && ` ${userData.fullName}`}!</p>
                        <p className="SelfieFan__Fan__Text--Center">Un momento... estamos guardando tu puntuación.</p>
                        <p className="SelfieFan__Fan__Text--Center">{finalScore} puntos</p>
                        <p className="SelfieFan__Fan__Text--Center">No salgas de esta pantalla.</p>

                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a>
                    </footer>
                </>
            )}

            {step === 'done' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mb-0">¡Muy bien!</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Has logrado {finalScore} puntos</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Muchas gracias por participar</p> */}
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje ha sido enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>¡{finalScore} puntos!</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p> */}
                        <p className='Messages__Fan__Text--Center mb-0'>Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.</p>
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a>
                    </footer>
                </>
            )}

            {step === 'played' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mt-3 mb-0">Gracias por tu participación</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje fue enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a>
                    </footer>
                </>
            )}

            {step === 'resendPoints' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-danger"><i className="bi bi-x-circle-fill"></i></p>
                        <p className="mb-0">No ha sido posible enviar tu puntaje. Inténtalo nuevamente con el botón Enviar mi puntaje</p>
                        <button type='button' className='Btn__Adsl--RedOutlineTransparent mx-auto' onClick={() => ResendPoints()}>Enviar mi puntaje</button>
                        {/* <p className="mb-0">No ha sido posible guardar tu puntaje. Inténtalo nuevamente con el botón Guardar mi puntaje</p>
                        <button type='button' className='Btn__Adsl--RedOutlineTransparent mx-auto' onClick={() => ResendPoints()}>Guardar mi puntaje</button> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a>
                    </footer>
                </>
            )}

            {step === 'inactive' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-danger"><i className="bi bi-check-circle-fill text-danger"></i></p>
                        <p className="SelfieFan__Sent__Title mb-0 text-danger">Dinámica finalizada</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Gracias por tu interés y participación. Se han cerrado los registros.</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Te invitamos a participar en las siguientes dinámicas que tendremos para ti.</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>El registro y acceso al juego se encuentran temporalmente cerrados.</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Por favor, espera indicaciones en la pantalla del estadio.</p> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_adsl' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_adsl' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a>
                    </footer>
                </>
            )}
        </div>
    );
};

export { PluckingRooster };